import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {Map as ImmutableMap} from 'immutable';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getCalculatedBenefitAmounts} from '../../PlanTemplates/ACC/GVA6';
import {unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {dislocationFractureRiderPath} from './PropertyPaths/dislocationFractureRiderPath';

const DislocationFractureRiderDetails = () => {
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const propertyPaths = dislocationFractureRiderPath(selectedPlan?.planIndex ?? 0);
  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const standardBenefitStates = ['CO', 'DC', 'KY', 'MD', 'MN', 'RI', 'WA'];

  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) === 'GVA6';

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'dislocation-rider-row-1'},
      items: [
        {
          key: propertyPaths.unitsOfDislocationRider,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              options={unitsOptions}
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfDislocationRider)}
              id={propertyPaths.unitsOfDislocationRider}
              required
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.scheduleOfDislocationRider"
              // options={riderOptions}
              label={isGVA6Plan ? 'Rider Type' : 'Schedule'}
              value={getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider)}
              id={propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider}
              onChange={
                e =>
                  updateQuoteFieldValues([
                    {
                      path: propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider,
                      newValue: e.target.value,
                    },
                    {
                      path: propertyPaths.dislocationFractureRiderDetails.timesMaximumOpenBenefit,
                      newValue: defaultPlanValues
                        ?.get('GVA6')
                        .get('ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit'),
                    },
                    {
                      path: propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumOpenBenefit,
                      newValue: defaultPlanValues
                        ?.get('GVA6')
                        .get('ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit'),
                    },
                    {
                      path: propertyPaths.dislocationFractureRiderDetails.maximumOpenReductionBenefit,
                      newValue: getCalculatedBenefitAmounts(
                        getQuoteFieldValue<number>(propertyPaths.unitsOfDislocationRider)
                      ).maximumOpenReductionBenefitAmount,
                    },
                    {
                      path: propertyPaths.fractureDetails.chipFracture,
                      newValue: e.target.value == 'Standard' ? 0.0 : 0.25,
                    },
                    {
                      path: propertyPaths.fractureDetails.stressFractures,
                      newValue: e.target.value == 'Standard' ? 0.0 : 0.1,
                    },
                  ])
                // updateQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider, e.target.value)
              }
              required
              disabled={!isGVA6Plan || standardBenefitStates.includes(situsStateCd)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-rider-row-2'},
      items: [
        {
          key: propertyPaths.dislocationFractureRiderDetails.dependentChildOfDislocationRider,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.dependentChildOfDislocationRider"
              label="Dependent Child"
              value={getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.dependentChildOfDislocationRider)}
              id={propertyPaths.dislocationFractureRiderDetails.dependentChildOfDislocationRider}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-rider-row-3'},
      items: [
        {
          key: propertyPaths.dislocationFractureRiderDetails.timesMaximumClosedBenefit,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.timesMaximumClosedBenefit"
              label="# of Times Maximum Closed Benefit is Payable per Time Period"
              value={getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.timesMaximumClosedBenefit)}
              id={propertyPaths.dislocationFractureRiderDetails.timesMaximumClosedBenefit}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumClosedBenefit,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.timePeroidMaximumClosedBenefit"
              label="Time Period"
              value={getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumClosedBenefit)}
              id={propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumClosedBenefit}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-rider-row-4'},
      items: [
        {
          key: propertyPaths.dislocationFractureRiderDetails.timesMaximumOpenBenefit,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit"
              label="# of Times Maximum Open Benefit is Payable per Time Period"
              value={
                isGVA6Plan &&
                getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
                  'Standard'
                  ? ''
                  : getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.timesMaximumOpenBenefit)
              }
              id={propertyPaths.dislocationFractureRiderDetails.timesMaximumOpenBenefit}
              required={
                !(
                  isGVA6Plan &&
                  getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
                    'Standard'
                )
              }
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumOpenBenefit,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit"
              label="Time Period"
              value={
                isGVA6Plan &&
                getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
                  'Standard'
                  ? ''
                  : getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumOpenBenefit)
              }
              id={propertyPaths.dislocationFractureRiderDetails.timePeroidMaximumOpenBenefit}
              required={
                !(
                  isGVA6Plan &&
                  getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
                    'Standard'
                )
              }
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-rider-row-5'},
      items: [
        {
          key: propertyPaths.dislocationFractureRiderDetails.maximumClosedReductionBenefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Closed Reduction Benefit"
              disabled
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.maximumClosedReductionBenefitAmount)
              )}
              id={propertyPaths.dislocationFractureRiderDetails.maximumClosedReductionBenefitAmount}
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider,
          element:
            isGVA6Plan &&
            getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
              'Standard' ? (
              <QuoteFormElement
                elementType="Text"
                label="Maximum Open Reduction Benefit"
                value=""
                id={propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider}
                disabled
                required={!isGVA6Plan}
              />
            ) : (
              <QuoteFormElement
                elementType="Text"
                label="Maximum Open Reduction Benefit"
                value={formatCurrencyValue(
                  getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.maximumOpenReductionBenefitAmount)
                )}
                id={propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider}
                disabled
                required={
                  !(
                    isGVA6Plan &&
                    getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) ==
                      'Standard'
                  )
                }
                adornment="money"
              />
            ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const DislocationDetails = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = dislocationFractureRiderPath(selectedPlan?.planIndex ?? 0);
  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) == 'GVA6';
  const isScheduleRiderStandard =
    getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) == 'Standard';
  const redInd1 = 1;
  const redInd2 = 0;

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'dislocation-details-row-1'},
      items: [
        {
          key: propertyPaths.dislocationDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue(propertyPaths.dislocationDetails.incurralPeriod)}
              id={propertyPaths.dislocationDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-details-row-2'},
      items: [
        {
          key: 'heading',
          element: (
            <div>
              {isGVA6Plan && isScheduleRiderStandard ? <h3>Dislocation Schedule</h3> : <h3>Closed Reduction Amount</h3>}
            </div>
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'heading',
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <div>
              <h3>Open Reduction Amount</h3>
            </div>
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-details-row-3'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].hipAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hip Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].hipAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].hipAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].hipAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hip Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].hipAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].hipAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-4'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneeNoPatellaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Knee Joint (Except Patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneeNoPatellaAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneeNoPatellaAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneeNoPatellaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Knee Joint (Except Patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneeNoPatellaAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneeNoPatellaAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-5'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].footBonesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bone or Bones of the Foot (Except Toes)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].footBonesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].footBonesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].footBonesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bone or Bones of the Foot (Except Toes)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].footBonesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].footBonesAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-6'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].ankleAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ankle Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].ankleAmount)
              )}
              id={`dislocationDetails.reductionAmountDetails[${redInd1}].ankleAmount`}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].ankleAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ankle Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].ankleAmount)
              )}
              id={`dislocationDetails.reductionAmountDetails[${redInd2}].ankleAmount`}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-7'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].wristAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Wrist Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].wristAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].wristAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].wristAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Wrist Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].wristAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].wristAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-8'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].elbowAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Elbow Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].elbowAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].elbowAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].elbowAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Elbow Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].elbowAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].elbowAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-9'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].shoulderGlenohumeralAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Shoulder Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].shoulderGlenohumeralAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].shoulderGlenohumeralAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].shoulderGlenohumeralAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Shoulder Joint"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].shoulderGlenohumeralAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].shoulderGlenohumeralAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-10'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].handBonesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bone or Bones of the Hand (Except Fingers)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].handBonesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].handBonesAmount}
              disabled
              adornment="money"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].handBonesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bone or Bones of the Hand (Except Fingers)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].handBonesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].handBonesAmount}
              adornment="money"
              disabled
              required={!isGVA6Plan}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-11'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].collarboneAcromioclavicularAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Collar Bone"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].collarboneAcromioclavicularAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].collarboneAcromioclavicularAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].collarboneAcromioclavicularAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Collar Bone"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].collarboneAcromioclavicularAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].collarboneAcromioclavicularAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-12'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreFingersAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or more Fingers"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreFingersAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreFingersAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreFingersAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or more Fingers"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreFingersAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreFingersAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-13'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreToesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or more Toes"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreToesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].twoOrMoreToesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreToesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or more Toes"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreToesAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].twoOrMoreToesAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-14'},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].oneFingerOrToeAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Finger or One Toe"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].oneFingerOrToeAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].oneFingerOrToeAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].oneFingerOrToeAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Finger or One Toe"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].oneFingerOrToeAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].oneFingerOrToeAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-15', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneecapAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Kneecap (Patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneecapAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].kneecapAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneecapAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Kneecap (Patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneecapAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].kneecapAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-16', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].lowerJawAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Lower Jaw"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].lowerJawAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].lowerJawAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].lowerJawAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Lower Jaw"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].lowerJawAmount)
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].lowerJawAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-17', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].otherDislocationAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Any other Dislocation"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].otherDislocationAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd1].otherDislocationAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].otherDislocationAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Any other Dislocation"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].otherDislocationAmount
                )
              )}
              id={propertyPaths.dislocationDetails.reductionAmountDetails[redInd2].otherDislocationAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'dislocation-row-18'},
      items: [
        {
          key: propertyPaths.dislocationDetails.partialDislocation,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCDislocationBenefit.partialDislocation"
              label="Partial Dislocation"
              value={getQuoteFieldValue(propertyPaths.dislocationDetails.partialDislocation)}
              disabled
              id={propertyPaths.dislocationDetails.partialDislocation}
              required={!isGVA6Plan}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const FractureDetails = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = dislocationFractureRiderPath(selectedPlan?.planIndex ?? 0);
  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) == 'GVA6';
  const isScheduleRiderStandard =
    getQuoteFieldValue(propertyPaths.dislocationFractureRiderDetails.scheduleOfDislocationRider) == 'Standard';
  const redInd1 = 1;
  const redInd2 = 0;

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'fracture-details-row-1'},
      items: [
        {
          key: propertyPaths.fractureDetails.incurralPeriod,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue(propertyPaths.fractureDetails.incurralPeriod)}
              id={propertyPaths.fractureDetails.incurralPeriod}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-details-row-2'},
      items: [
        {
          key: 'heading',
          element: (
            <div>
              {isGVA6Plan && isScheduleRiderStandard ? <h3>Fracture Schedule</h3> : <h3>Closed Reduction Amount</h3>}
            </div>
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'heading',
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <div>
              <h3>Open Reduction Amount</h3>
            </div>
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-3'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].hipAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hip, Thigh (femur)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].hipAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].hipAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].hipAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hip, Thigh (femur)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].hipAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].hipAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-4'},
      items: [
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
              .skullFractureNonDepressedNoFaceNoseBonesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Skull Fracture - Non-Depressed (except bones of face and nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                    .skullFractureNonDepressedNoFaceNoseBonesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                  .skullFractureNonDepressedNoFaceNoseBonesAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
              .skullFractureNonDepressedNoFaceNoseBonesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Skull Fracture - Non-Depressed (except bones of face and nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                    .skullFractureNonDepressedNoFaceNoseBonesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                  .skullFractureNonDepressedNoFaceNoseBonesAmount
              }
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-5'},
      items: [
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
              .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Pelvis (except Coccyx)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                    .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                  .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
              .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Pelvis (except Coccyx)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                    .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                  .pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount
              }
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-6'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].upperArmBetwElbowAndShoulderHumerusAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Arm, between Shoulder and Elbow (humerus)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                    .upperArmBetwElbowAndShoulderHumerusAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1].upperArmBetwElbowAndShoulderHumerusAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].upperArmBetwElbowAndShoulderHumerusAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Arm, between Shoulder and Elbow (humerus)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                    .upperArmBetwElbowAndShoulderHumerusAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2].upperArmBetwElbowAndShoulderHumerusAmount
              }
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-7'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].shoulderBladeScapulaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Shoulder Blade (scapula)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].shoulderBladeScapulaAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].shoulderBladeScapulaAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].shoulderBladeScapulaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Shoulder Blade (scapula)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].shoulderBladeScapulaAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].shoulderBladeScapulaAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-8'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].legTibiaFibulaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Leg (tibia or fibula)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].legTibiaFibulaAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].legTibiaFibulaAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].legTibiaFibulaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Leg (tibia or fibula)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].legTibiaFibulaAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].legTibiaFibulaAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-9'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].ankleAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ankle"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].ankleAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].ankleAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].ankleAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ankle"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].ankleAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].ankleAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-10'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].kneecapPatellaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Kneecap (patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].kneecapPatellaAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].kneecapPatellaAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].kneecapPatellaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Kneecap (patella)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].kneecapPatellaAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].kneecapPatellaAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-11'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].collarboneClavicleSternumAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Collar Bone (clavicle)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].collarboneClavicleSternumAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].collarboneClavicleSternumAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].collarboneClavicleSternumAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Collar Bone (clavicle)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].collarboneClavicleSternumAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].collarboneClavicleSternumAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-12'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].forearmRadiusUlnaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Forearm (radius or ulna)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].forearmRadiusUlnaAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].forearmRadiusUlnaAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].forearmRadiusUlnaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Forearm (radius or ulna)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].forearmRadiusUlnaAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].forearmRadiusUlnaAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-13'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].footOrHeelNoToesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Foot (except toes)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].footOrHeelNoToesAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].footOrHeelNoToesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].footOrHeelNoToesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Foot (except toes)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].footOrHeelNoToesAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].footOrHeelNoToesAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-14'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].handNoFingersAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hand or Wrist (except fingers)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].handNoFingersAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].handNoFingersAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].handNoFingersAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Hand or Wrist (except fingers)"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].handNoFingersAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].handNoFingersAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-15'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].lowerJawMandibleNoAlveolarProcessAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Lower Jaw / Mandible (except alveolar process)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].lowerJawMandibleNoAlveolarProcessAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].lowerJawMandibleNoAlveolarProcessAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].lowerJawMandibleNoAlveolarProcessAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Lower Jaw / Mandible (except alveolar process)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].lowerJawMandibleNoAlveolarProcessAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].lowerJawMandibleNoAlveolarProcessAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-16'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].twoOrMoreRibsFingersOrToesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or More Ribs, Fingers, or Toes"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].twoOrMoreRibsFingersOrToesAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].twoOrMoreRibsFingersOrToesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].twoOrMoreRibsFingersOrToesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Two or More Ribs, Fingers, or Toes"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].twoOrMoreRibsFingersOrToesAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].twoOrMoreRibsFingersOrToesAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-17'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].faceOrNoseBonesNoMandibleOrMaxillaAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bones of Face (excluding nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].faceOrNoseBonesNoMandibleOrMaxillaAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1].faceOrNoseBonesNoMandibleOrMaxillaAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].faceOrNoseBonesNoMandibleOrMaxillaAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Bones of Face (excluding nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].faceOrNoseBonesNoMandibleOrMaxillaAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2].faceOrNoseBonesNoMandibleOrMaxillaAmount
              }
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-18'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].noseAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Nose"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].noseAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].noseAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].noseAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Nose"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].noseAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].noseAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-19'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].oneRibFingerOrToeAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Rib, Finger or Toe"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].oneRibFingerOrToeAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].oneRibFingerOrToeAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].oneRibFingerOrToeAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="One Rib, Finger or Toe"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].oneRibFingerOrToeAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].oneRibFingerOrToeAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-20'},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].coccyxAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Coccyx"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].coccyxAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].coccyxAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].coccyxAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Coccyx"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].coccyxAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].coccyxAmount}
              disabled
              required={!isGVA6Plan}
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-21', hidden: isGVA6Plan},
      items: [
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd1].skullFractureDepressedNoFaceNoseBonesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Skull Fracture - Depressed (except bones of face and nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                    .skullFractureDepressedNoFaceNoseBonesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                  .skullFractureDepressedNoFaceNoseBonesAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key:
            propertyPaths.fractureDetails.reductionAmountDetails[redInd2].skullFractureDepressedNoFaceNoseBonesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Skull Fracture - Depressed (except bones of face and nose)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                    .skullFractureDepressedNoFaceNoseBonesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                  .skullFractureDepressedNoFaceNoseBonesAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-22', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].vertebraeBodyOfNoVertebralProcessesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Vertebral Body"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1]
                    .vertebraeBodyOfNoVertebralProcessesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd1].vertebraeBodyOfNoVertebralProcessesAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].vertebraeBodyOfNoVertebralProcessesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Vertebral Body"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2]
                    .vertebraeBodyOfNoVertebralProcessesAmount
                )
              )}
              id={
                propertyPaths.fractureDetails.reductionAmountDetails[redInd2].vertebraeBodyOfNoVertebralProcessesAmount
              }
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-23', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].vertebralProcessesAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Vertebral Process"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].vertebralProcessesAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].vertebralProcessesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].vertebralProcessesAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Vertebral Process"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].vertebralProcessesAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].vertebralProcessesAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-23', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].upperJawMaxillaNoAlveolarProcessAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Upper Jaw (except alveolar process)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd1].upperJawMaxillaNoAlveolarProcessAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].upperJawMaxillaNoAlveolarProcessAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].upperJawMaxillaNoAlveolarProcessAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Upper Jaw (except alveolar process)"
              value={formatCurrencyValue(
                getQuoteFieldValue(
                  propertyPaths.fractureDetails.reductionAmountDetails[redInd2].upperJawMaxillaNoAlveolarProcessAmount
                )
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].upperJawMaxillaNoAlveolarProcessAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-24', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].sternumAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Sternum"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].sternumAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].sternumAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].sternumAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Sternum"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].sternumAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].sternumAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-25', hidden: isGVA6Plan},
      items: [
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd1].anyOtherFractureAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Any other fracture"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd1].anyOtherFractureAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd1].anyOtherFractureAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.reductionAmountDetails[redInd2].anyOtherFractureAmount,
          hidden: isGVA6Plan && isScheduleRiderStandard,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Any other fracture"
              value={formatCurrencyValue(
                getQuoteFieldValue(propertyPaths.fractureDetails.reductionAmountDetails[redInd2].anyOtherFractureAmount)
              )}
              id={propertyPaths.fractureDetails.reductionAmountDetails[redInd2].anyOtherFractureAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'fracture-row-26'},
      items: [
        {
          key: propertyPaths.fractureDetails.chipFracture,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCFractureBenefit.chipFracture"
              label="Chip/Avulsion Fractures"
              value={getQuoteFieldValue(propertyPaths.fractureDetails.chipFracture)}
              id={propertyPaths.fractureDetails.chipFracture}
              required={!isGVA6Plan}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.fractureDetails.stressFractures,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="ACCFractureBenefit.stressFractures"
              label="Stress Fractures"
              value={getQuoteFieldValue(propertyPaths.fractureDetails.stressFractures)}
              id={propertyPaths.fractureDetails.stressFractures}
              required={!isGVA6Plan}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];
  return <FormRowGrid rows={rows} />;
};

export const AccidentDislocationAndFractureRiderBlock = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const {getQuoteFieldValue} = useQuoteField();
  const propertyPaths = dislocationFractureRiderPath(selectedPlan?.planIndex ?? 0);
  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) == 'GVA6';

  const isDislocationSelected = getQuoteFieldValue<boolean>(propertyPaths.isDislocationAndFractureRiderBenefitApplied);

  const sections: ConditionalSection[] = [
    {
      Component: DislocationFractureRiderDetails,
      key: 'DislocationAndFractureRider',
      label: 'DISLOCATION AND FRACTURE RIDER',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isDislocationAndFractureRiderBenefitApplied),
    },
    ...(isDislocationSelected
      ? [
          {
            Component: DislocationDetails,
            key: 'DislocationDetails',
            label: isGVA6Plan ? 'DISLOCATION' : 'COMPLETE DISLOCATION',
            disabled: true,
            isApplied: isDislocationSelected,
          },
          {
            Component: FractureDetails,
            key: 'FractureDetails',
            disabled: true,
            label: 'FRACTURE',
            isApplied: isDislocationSelected,
          },
        ]
      : []),
  ];

  return (
    <FormWrapper title="Dislocation and Fracture Rider" collapsible withSeparator>
      <Grid container alignItems="center">
        <QuoteFormConditionalSection sections={sections} />
      </Grid>
    </FormWrapper>
  );
};
